import { create } from 'zustand';
import * as api from '../utils/api';

/**
 * Phase 24 — top-level account mode (Personal / Fleet).
 *
 * Distinct from per-scenario `scenario.mode` (which lives in useScenarioStore
 * and is locked at scenario creation). The account-mode is the master nav-
 * level state and gates which feature surfaces render across the app.
 *
 * Persistence:
 *   - Authenticated users: GET/PATCH /api/profile.
 *   - Unauthenticated users: localStorage 'vehicletco_app_mode'.
 *
 * UI label mapping: 'individual' → "Personal", 'fleet' → "Fleet".
 *
 * Phase 25.5 — extended with onboarding-tour state. Tour-seen flags use
 * the same persistence pattern (Supabase for authed users, localStorage
 * fallback for guests). `tourActive` is process-local — never persisted.
 */
export type AppMode = 'individual' | 'fleet';

// Phase 26 Part 2: subscription tier gating fleet access.
export type Tier = 'free' | 'promo' | 'paid';

// v1.42.4 Fix 3: user's mode preference at login. "ask" preserves the
// welcome-page-on-login behavior; "individual"/"fleet" auto-applies.
export type DefaultMode = 'individual' | 'fleet' | 'ask';

const LOCAL_STORAGE_KEY = 'vehicletco_app_mode';
const LOCAL_PERSONAL_TOUR_KEY = 'vt_personal_tour_seen';
const LOCAL_FLEET_TOUR_KEY = 'vt_fleet_tour_seen';
// Phase 25.6 — duplicated from WelcomePage.tsx to avoid a circular import.
// Keep the two in sync.
const LOCAL_WELCOME_COMPLETED_KEY = 'vehicletco_welcome_completed';
const LOCAL_PENDING_MODE_KEY = 'vehicletco_pending_mode_apply';

/**
 * Phase 25.6 — pop a pending mode pick stashed by WelcomePage before the
 * user signed in. Returns the mode if valid; clears the key either way.
 */
function consumePendingMode(): AppMode | null {
  try {
    const raw = localStorage.getItem(LOCAL_PENDING_MODE_KEY);
    if (raw === 'individual' || raw === 'fleet') {
      localStorage.removeItem(LOCAL_PENDING_MODE_KEY);
      return raw;
    }
  } catch {
    // ignore — quota or privacy mode
  }
  return null;
}

/**
 * Phase 25.6 — auto-seed the welcome-completed flag for pre-25.6 users so
 * they don't get re-routed through /welcome on their next visit.
 *
 * Engagement signals (any one is enough):
 *   - Either tour has been completed (personal_tour_seen / fleet_tour_seen)
 *   - User explicitly picked Fleet mode in their profile
 *
 * Idempotent. Safe to call on every initialize() resolution.
 */
function seedWelcomeCompletedIfEngaged(
  personalSeen: boolean,
  fleetSeen: boolean,
  mode: AppMode,
): void {
  try {
    if (localStorage.getItem(LOCAL_WELCOME_COMPLETED_KEY) === 'true') return;
    if (personalSeen || fleetSeen || mode === 'fleet') {
      localStorage.setItem(LOCAL_WELCOME_COMPLETED_KEY, 'true');
    }
  } catch {
    // ignore — quota or privacy mode
  }
}

function readLocalMode(): AppMode {
  try {
    const v = localStorage.getItem(LOCAL_STORAGE_KEY);
    return v === 'fleet' ? 'fleet' : 'individual';
  } catch {
    return 'individual';
  }
}

function writeLocalMode(mode: AppMode): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, mode);
  } catch {
    // ignore — quota or privacy mode
  }
}

function readLocalTourSeen(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeLocalTourSeen(key: string, seen: boolean): void {
  try {
    localStorage.setItem(key, seen ? 'true' : 'false');
  } catch {
    // ignore — quota or privacy mode
  }
}

interface AppModeStore {
  activeMode: AppMode;
  initialized: boolean;
  loading: boolean;
  isFleet: boolean;
  isIndividual: boolean;
  // Phase 25.5 — onboarding tour state.
  personalTourSeen: boolean;
  fleetTourSeen: boolean;
  tourActive: boolean;
  // Phase 26 Part 2 — subscription tier state.
  tier: Tier;
  isFleetTierActive: boolean;
  // UX audit fix — true when the last profile load failed (cold start / network).
  // Distinguishes "tier unknown because we couldn't reach the server" from a
  // genuine free tier, so surfaces don't render fallback values as fact and the
  // fleet upsell gate isn't shown on a fetch failure.
  profileError: boolean;
  // Phase 26 Part 2 — fleet-gate modal control. Toggled by the mode-switch
  // and compute-trigger handlers; consumed by the gate modal rendered in
  // Layout so any surface that requests fleet access shows the same UI.
  fleetGateOpen: boolean;
  // v1.42.4 Fix 3 — user's default mode preference (loaded from /api/profile).
  defaultMode: DefaultMode;
  /** Set tier (and the derived isFleetTierActive flag). */
  initTier: (tier: Tier) => void;
  /** Open/close the gate modal. */
  setFleetGateOpen: (open: boolean) => void;
  /** Persist default mode preference (authed users → PATCH /api/profile). */
  setDefaultMode: (mode: DefaultMode) => Promise<void>;
  /**
   * Load activeMode from /api/profile (or localStorage when unauthenticated).
   * Also pulls tour-seen flags so the auto-start gate can fire on first
   * paint. Idempotent — calling more than once is safe.
   */
  initialize: (isAuthenticated: boolean) => Promise<void>;
  /**
   * Set activeMode and persist:
   *   - Authenticated → PATCH /api/profile.
   *   - Unauthenticated → localStorage only.
   * Network failures are swallowed so the UI stays responsive; the local
   * state still updates so the user sees the toggle move.
   */
  setMode: (mode: AppMode, isAuthenticated: boolean) => Promise<void>;
  /**
   * Mark the tour for the given mode as seen (or unseen, for replay) and
   * persist. Authed users → PATCH /api/profile. Guests → localStorage.
   */
  markTourSeen: (
    mode: AppMode,
    seen: boolean,
    isAuthenticated: boolean,
  ) => Promise<void>;
  /** Controls whether the tour is currently running (process-local). */
  setTourActive: (active: boolean) => void;
  /**
   * Returns true when the tour for the current activeMode has not been seen.
   * Used by VehicleTCOTour to decide whether to auto-start.
   */
  shouldAutoStartTour: () => boolean;
}

export const useAppModeStore = create<AppModeStore>((set, get) => ({
  activeMode: 'individual',
  initialized: false,
  loading: false,
  isFleet: false,
  isIndividual: true,
  personalTourSeen: false,
  fleetTourSeen: false,
  tourActive: false,
  tier: 'free',
  isFleetTierActive: false,
  profileError: false,
  fleetGateOpen: false,
  defaultMode: 'ask',

  initTier: (tier: Tier) => {
    const safe: Tier = tier === 'promo' || tier === 'paid' ? tier : 'free';
    set({ tier: safe, isFleetTierActive: safe !== 'free' });
  },

  setFleetGateOpen: (open: boolean) => set({ fleetGateOpen: open }),

  setDefaultMode: async (mode: DefaultMode) => {
    if (mode !== 'individual' && mode !== 'fleet' && mode !== 'ask') return;
    set({ defaultMode: mode });
    try {
      await api.patchProfile({ default_mode: mode });
    } catch {
      // Best-effort persistence; the local state still reflects the user's
      // choice for the rest of the session.
    }
  },

  initialize: async (isAuthenticated: boolean) => {
    // Re-runs on auth flips: signing in pulls the persisted mode; signing
    // out drops back to localStorage default. The initial Layout render
    // before this resolves is gated by the `initialized` flag.
    set({ loading: true, profileError: false });
    if (!isAuthenticated) {
      const mode = readLocalMode();
      const personalSeen = readLocalTourSeen(LOCAL_PERSONAL_TOUR_KEY);
      const fleetSeen = readLocalTourSeen(LOCAL_FLEET_TOUR_KEY);
      seedWelcomeCompletedIfEngaged(personalSeen, fleetSeen, mode);
      set({
        activeMode: mode,
        isFleet: mode === 'fleet',
        isIndividual: mode === 'individual',
        personalTourSeen: personalSeen,
        fleetTourSeen: fleetSeen,
        initialized: true,
        loading: false,
        profileError: false,
      });
      return;
    }
    try {
      const profile = await api.getProfile();
      const profileTier: Tier =
        profile.tier === 'paid' || profile.tier === 'promo' ? profile.tier : 'free';
      const tierAllowsFleet = profileTier !== 'free';
      // v1.42.4 Fix 3: respect the user's default-mode preference. "ask"
      // preserves the welcome-page flow; "individual"/"fleet" auto-applies
      // and seeds welcome_completed so the user lands directly in the right
      // mode on every fresh login.
      const rawDefault =
        profile.default_mode === 'individual' ||
        profile.default_mode === 'fleet' ||
        profile.default_mode === 'ask'
          ? profile.default_mode
          : 'ask';
      let defaultMode: DefaultMode = rawDefault;
      // Free-tier users cannot default to Fleet — fall back to "ask" so the
      // first attempt still routes through the gate modal.
      if (defaultMode === 'fleet' && !tierAllowsFleet) {
        defaultMode = 'ask';
      }
      let mode: AppMode = profile.app_mode === 'fleet' ? 'fleet' : 'individual';
      // Apply the default-mode preference BEFORE the free-tier reconciliation
      // so the user's saved preference wins over the stale app_mode column.
      if (defaultMode === 'individual' || defaultMode === 'fleet') {
        mode = defaultMode;
        try {
          localStorage.setItem(LOCAL_WELCOME_COMPLETED_KEY, 'true');
        } catch {
          // ignore — quota or privacy mode
        }
      }
      // Phase 26.2: a free-tier user with `app_mode=fleet` on file (set by a
      // previous gate-bypassing bug, or by a downgrade) should not silently
      // land in Fleet. Force them back to Personal and pop the gate so the
      // next interaction routes through redemption.
      let gateOnLoad = false;
      if (mode === 'fleet' && !tierAllowsFleet) {
        mode = 'individual';
        gateOnLoad = true;
      }
      // Phase 25.6: if the user picked a mode on /welcome before signing in,
      // apply it now. Best-effort PATCH — on failure we still honor the
      // choice locally so the user lands in the right mode this session.
      // Phase 26.2: if the pending pick is Fleet and the user is free-tier,
      // discard the pending pick and pop the gate instead of writing 'fleet'
      // to the profile.
      const pending = consumePendingMode();
      if (pending && pending !== mode) {
        if (pending === 'fleet' && !tierAllowsFleet) {
          gateOnLoad = true;
        } else {
          try {
            await api.patchProfile({ app_mode: pending });
          } catch {
            // PATCH failed — the local mode update below still takes effect;
            // the user can re-toggle from the nav if it sticks wrong.
          }
          mode = pending;
        }
      }
      const personalSeen = !!profile.personal_tour_seen;
      const fleetSeen = !!profile.fleet_tour_seen;
      // Phase 25.6: mirror authenticated tour-seen values down to localStorage
      // so the catch branch (transient API failure) can recover the user's
      // last-known state on this device instead of resetting to false.
      writeLocalTourSeen(LOCAL_PERSONAL_TOUR_KEY, personalSeen);
      writeLocalTourSeen(LOCAL_FLEET_TOUR_KEY, fleetSeen);
      seedWelcomeCompletedIfEngaged(personalSeen, fleetSeen, mode);
      set({
        activeMode: mode,
        isFleet: mode === 'fleet',
        isIndividual: mode === 'individual',
        personalTourSeen: personalSeen,
        fleetTourSeen: fleetSeen,
        tier: profileTier,
        isFleetTierActive: tierAllowsFleet,
        profileError: false,
        fleetGateOpen: gateOnLoad,
        defaultMode,
        initialized: true,
        loading: false,
      });
    } catch {
      // Phase 25.6: profile lookup failed (Render cold start, transient 5xx,
      // network blip). Fall back to localStorage tour-seen flags rather than
      // resetting them to false — that previously caused the tour to
      // auto-start on every reload whenever the API was momentarily unhappy,
      // even for users who had completed the tour and had Supabase
      // personal_tour_seen=true / fleet_tour_seen=true on file.
      set({
        activeMode: 'individual',
        isFleet: false,
        isIndividual: true,
        personalTourSeen: readLocalTourSeen(LOCAL_PERSONAL_TOUR_KEY),
        fleetTourSeen: readLocalTourSeen(LOCAL_FLEET_TOUR_KEY),
        initialized: true,
        loading: false,
        // Tier is left untouched (we never assert a value we couldn't load);
        // profileError signals downstream surfaces that the tier/plan is
        // UNKNOWN, not free, so they show a retry instead of fallback-as-fact.
        profileError: true,
      });
    }
  },

  setMode: async (mode: AppMode, isAuthenticated: boolean) => {
    if (mode !== 'individual' && mode !== 'fleet') return;
    if (get().activeMode === mode) return;
    // Phase 26 Part 2: a free-tier user clicking the Fleet toggle pops the
    // gate modal instead of actually flipping modes. The modal handles
    // redemption + tier upgrade; on success it calls setMode again with the
    // new tier so this branch is no longer taken.
    if (mode === 'fleet' && isAuthenticated && !get().isFleetTierActive) {
      set({ fleetGateOpen: true });
      return;
    }
    set({
      activeMode: mode,
      isFleet: mode === 'fleet',
      isIndividual: mode === 'individual',
    });
    if (!isAuthenticated) {
      writeLocalMode(mode);
      return;
    }
    try {
      await api.patchProfile({ app_mode: mode });
    } catch {
      // Persistence failed — leave the UI in the new mode, but mirror to
      // localStorage so a refresh recovers the user's choice if they're
      // still online with the same browser.
      writeLocalMode(mode);
    }
  },

  markTourSeen: async (
    mode: AppMode,
    seen: boolean,
    isAuthenticated: boolean,
  ) => {
    // Local state updates immediately so the auto-start guard never re-fires
    // mid-session. Persistence runs after.
    if (mode === 'individual') {
      set({ personalTourSeen: seen });
      if (!isAuthenticated) {
        writeLocalTourSeen(LOCAL_PERSONAL_TOUR_KEY, seen);
        return;
      }
      try {
        await api.patchProfile({ personal_tour_seen: seen });
      } catch {
        writeLocalTourSeen(LOCAL_PERSONAL_TOUR_KEY, seen);
      }
    } else {
      set({ fleetTourSeen: seen });
      if (!isAuthenticated) {
        writeLocalTourSeen(LOCAL_FLEET_TOUR_KEY, seen);
        return;
      }
      try {
        await api.patchProfile({ fleet_tour_seen: seen });
      } catch {
        writeLocalTourSeen(LOCAL_FLEET_TOUR_KEY, seen);
      }
    }
  },

  setTourActive: (active: boolean) => set({ tourActive: active }),

  shouldAutoStartTour: () => {
    const { isFleet, personalTourSeen, fleetTourSeen, tourActive } = get();
    if (tourActive) return false;
    return isFleet ? !fleetTourSeen : !personalTourSeen;
  },
}));
